import { PluginListenerHandle } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  KeyboardExtension,
  KeyboardExtensionEvent,
} from '../../plugins/keyboard.plugin';

interface NumericKeyboardResult {
  handleDefaultInputFocus: () => void;
  handleNumberInputFocus: () => void;
  handleInputBlur: () => void;
}

interface NumericKeyboardProps {
  onValueChange?: (value: string) => void;
  getCurrentValue?: () => string;
}

const useNumericKeyboard = ({
  onValueChange,
  getCurrentValue,
}: NumericKeyboardProps): NumericKeyboardResult => {
  const [isFocused, setIsFocused] = useState(false);
  const toggleListenerRef = useRef<PluginListenerHandle | null>(null);
  const doneListenerRef = useRef<PluginListenerHandle | null>(null);

  const handleNegativeToggle = useCallback(() => {
    if (getCurrentValue && onValueChange) {
      const currentValue = getCurrentValue();
      const numericValue = parseFloat(currentValue) || 0;
      const toggledValue = -numericValue;
      onValueChange(toggledValue.toString());
    }
  }, [getCurrentValue, onValueChange]);

  // Subscribe to keyboard events only when the input is focused
  useEffect(() => {
    if (isFocused) {
      KeyboardExtension.addListener(
        KeyboardExtensionEvent.TOGGLE_NEGATIVE_TAPPED,
        handleNegativeToggle
      ).then((listener) => {
        toggleListenerRef.current = listener;
      });

      KeyboardExtension.addListener(
        KeyboardExtensionEvent.ACCESSORY_DONE_TAPPED,
        () => {
          Keyboard.hide();
          KeyboardExtension.hideAccessoryBar();
        }
      ).then((listener) => {
        doneListenerRef.current = listener;
      });
    } else {
      if (toggleListenerRef.current) {
        toggleListenerRef.current.remove();
      }
      if (doneListenerRef.current) {
        doneListenerRef.current.remove();
      }
    }

    // Cleanup listeners on unmount
    return () => {
      if (toggleListenerRef.current) {
        toggleListenerRef.current.remove();
      }
      if (doneListenerRef.current) {
        doneListenerRef.current.remove();
      }
    };
  }, [getCurrentValue, handleNegativeToggle, isFocused, onValueChange]);

  // --- Input Focus Handlers ---

  // Called when focusing on an input that should use the DEFAULT accessory bar.
  const handleDefaultInputFocus = useCallback(() => {
    // Ensure the default bar is visible.
    Keyboard.setAccessoryBarVisible({ isVisible: true });
    // And ensure our custom bar is not visible.
    KeyboardExtension.hideAccessoryBar();
  }, []);

  // Called when focusing on the input that should use our custom bar.
  const handleNumberInputFocus = useCallback(() => {
    setIsFocused(true);
    Keyboard.setAccessoryBarVisible({ isVisible: false });
    KeyboardExtension.showAccessoryBar();
  }, []);

  // Called when the input loses focus
  const handleInputBlur = useCallback(() => {
    KeyboardExtension.hideAccessoryBar();
    setIsFocused(false);
  }, []);

  return useMemo(
    () => ({
      handleDefaultInputFocus,
      handleNumberInputFocus,
      handleInputBlur,
    }),
    [handleDefaultInputFocus, handleNumberInputFocus, handleInputBlur]
  );
};

export default useNumericKeyboard;
